import React, { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';

/**
 * Persistent install banner — shown at TOP of every authenticated page
 * as long as the PWA is not installed. Discreet, dismissible per-session
 * but reappears until installed.
 */
export function PWAInstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('lb_pwa_dismiss') === '1');
  const [installed, setInstalled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  });

  useEffect(() => {
    const beforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); localStorage.setItem('lb_pwa_installed', '1'); };
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    if (localStorage.getItem('lb_pwa_installed') === '1') setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed || hidden) return null;

  const install = async () => {
    if (!prompt) {
      // iOS Safari — no beforeinstallprompt, show hint
      alert('Sur iPhone/iPad :\n1. Appuyez sur le bouton Partager (carré + flèche)\n2. Faites défiler et sélectionnez « Sur l\'écran d\'accueil »');
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
      localStorage.setItem('lb_pwa_installed', '1');
    }
    setPrompt(null);
  };

  const dismiss = () => {
    sessionStorage.setItem('lb_pwa_dismiss', '1');
    setHidden(true);
  };

  return (
    <div data-testid="pwa-install-banner" className="w-full bg-gradient-to-r from-[#1677FF] to-[#0E62DB] text-white">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Download className="w-4 h-4 shrink-0" />
          <span className="truncate">
            <span className="font-semibold">Ajoutez LoyerBook à votre écran d&apos;accueil</span>
            <span className="hidden sm:inline"> pour une expérience optimale.</span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="px-3 py-1 rounded-md bg-white text-[#0E62DB] text-xs font-semibold hover:bg-slate-50"
          >
            Installer
          </button>
          <button onClick={dismiss} className="p-1 hover:bg-white/10 rounded" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
